import { cn } from '../utils/cn';

export type PageSkeletonVariant = 'page' | 'table' | 'panel' | 'cards';

export interface PageSkeletonProps {
  variant?: PageSkeletonVariant;
  className?: string;
}

/** Layout placeholder shown while a page/section API request is in flight. */
export function PageSkeleton({ variant = 'page', className }: PageSkeletonProps) {
  if (variant === 'panel') {
    return (
      <div className={cn('w-full animate-pulse space-y-4 py-2', className)} aria-busy="true" aria-hidden>
        <div className="h-8 w-48 rounded-lg bg-[hsl(var(--muted))]/40" />
        <div className="h-24 rounded-2xl bg-[hsl(var(--muted))]/30" />
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 rounded-xl bg-[hsl(var(--muted))]/25" />
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'table') {
    return (
      <div className={cn('w-full animate-pulse space-y-4', className)} aria-busy="true" aria-hidden>
        <div className="h-10 w-64 max-w-full rounded-xl bg-[hsl(var(--muted))]/40" />
        <div className="h-12 rounded-xl bg-[hsl(var(--muted))]/30" />
        <div className="overflow-hidden rounded-2xl border border-[hsl(var(--border))]/40">
          <div className="h-12 bg-[hsl(var(--muted))]/35" />
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-14 border-t border-[hsl(var(--border))]/30 bg-[hsl(var(--muted))]/20"
            />
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'cards') {
    return (
      <div className={cn('w-full animate-pulse space-y-6', className)} aria-busy="true" aria-hidden>
        <div className="h-28 rounded-2xl bg-[hsl(var(--muted))]/40" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 rounded-2xl bg-[hsl(var(--muted))]/30" />
          ))}
        </div>
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-[hsl(var(--muted))]/25" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('w-full animate-pulse space-y-6', className)} aria-busy="true" aria-hidden>
      <div className="space-y-3 rounded-xl border border-[hsl(var(--border))]/40 bg-[hsl(var(--card))]/40 p-5">
        <div className="h-3 w-40 rounded bg-[hsl(var(--muted))]/40" />
        <div className="h-8 w-72 max-w-full rounded-lg bg-[hsl(var(--muted))]/45" />
        <div className="h-4 w-96 max-w-full rounded bg-[hsl(var(--muted))]/30" />
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-28 rounded-2xl bg-[hsl(var(--muted))]/30" />
        ))}
      </div>
      <div className="h-14 rounded-2xl bg-[hsl(var(--muted))]/25" />
      <div className="space-y-2 rounded-2xl border border-[hsl(var(--border))]/40 p-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-14 rounded-xl bg-[hsl(var(--muted))]/20" />
        ))}
      </div>
    </div>
  );
}

export interface TableBodySkeletonProps {
  rows?: number;
  cols?: number;
}

/** Skeleton rows for use inside an existing `<tbody>`. */
export function TableBodySkeleton({ rows = 6, cols = 5 }: TableBodySkeletonProps) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="animate-pulse">
          {Array.from({ length: cols }).map((_, c) => (
            <td key={c} className="px-6 py-4">
              <div
                className="h-4 rounded bg-[hsl(var(--muted))]/30"
                style={{ width: `${55 + ((r + c) % 4) * 10}%` }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
